import { useState, useEffect, useCallback, useRef } from "react";
import { Plus, MapPin, Tag, Clock, Calendar, Store, Save, Palette, Navigation } from "lucide-react";
import { OpeningHours, Shop } from "../types/Shop";
import { CreateCustomShopData } from "../types/User";
import { useAuth } from "../contexts/AuthContext";
import { useTags } from "../contexts/TagsContext";
import OSMAddressAutocomplete from "./OSMAddressAutocomplete";
import CustomShopImageBrowserModal from "./CustomShopImageBrowserModal";
import { createCustomMarker, MarkerConfig } from "./MapMarker";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { reverseGeocode } from "../utils/geocoding";
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from "@/components/ui/responsive-modal";
import { cn } from "@/lib/utils";

interface ShopModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (shop: Shop | CreateCustomShopData) => void;
  shop?: Shop | null; // If provided, we're editing; if null/undefined, we're creating
  userLocation?: [number, number] | null;
  isDarkMode?: boolean;
  isSubmitting?: boolean;
  showFilters?: boolean;
}

const DEFAULT_CUSTOM_SHOP_ICON = "https://ik.imagekit.io/robertcpl/custom-shop-icons/default-custom-shop.png";

const defaultOpeningHours: OpeningHours[] = [
  { dayOfWeek: "Monday", openTime: "09:00", closeTime: "17:00", isClosed: false },
  { dayOfWeek: "Tuesday", openTime: "09:00", closeTime: "17:00", isClosed: false },
  { dayOfWeek: "Wednesday", openTime: "09:00", closeTime: "17:00", isClosed: false },
  { dayOfWeek: "Thursday", openTime: "09:00", closeTime: "17:00", isClosed: false },
  { dayOfWeek: "Friday", openTime: "09:00", closeTime: "17:00", isClosed: false },
  { dayOfWeek: "Saturday", openTime: "10:00", closeTime: "16:00", isClosed: false },
  { dayOfWeek: "Sunday", openTime: null, closeTime: null, isClosed: true },
];

export default function ShopModal({
  isOpen,
  onClose,
  onSave,
  shop,
  userLocation,
  isDarkMode = false,
  isSubmitting = false,
  showFilters = false
}: ShopModalProps) {
  const [showImageBrowser, setShowImageBrowser] = useState(false);
  const [showOpeningHours, setShowOpeningHours] = useState(false);
  const [initialAddress, setInitialAddress] = useState<string>("");
  const [gpsStatus, setGpsStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const markerPreviewRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const { tags } = useTags();

  const isDesktop = useMediaQuery("(min-width: 768px)");

  const isEditing = !!shop;

  // For custom shops, only allow specific tags
  const allowedCustomShopTags = ['Honey', 'Vegetables', 'Misc'];
  const availableTags = isEditing && shop?.isCustom === false
    ? tags.map((tag) => tag.name) // For editing non-custom shops, allow all tags
    : allowedCustomShopTags; // For creating new custom shops or editing custom shops, only allow specific tags
  const modalTitle = isEditing ? "Edit Shop" : "Add Custom Shop";
  const submitButtonText = isEditing ? "Save Changes" : "Create Shop";

  // Initialize form data based on whether we're editing or creating
  const getInitialFormData = useCallback((): Shop | CreateCustomShopData => {
    if (isEditing && shop) {
      // When editing, ensure all 7 days are present and isClosed is properly set
      const editingShop = { ...shop };

      // Create a map of existing opening hours by day
      const existingHoursByDay: { [key: string]: OpeningHours } = {};
      if (editingShop.openingHours) {
        editingShop.openingHours.forEach(hours => {
          existingHoursByDay[hours.dayOfWeek] = {
            ...hours,
            isClosed: hours.openTime === null && hours.closeTime === null
          };
        });
      }

      // Ensure all 7 days are present, using defaults for missing days
      editingShop.openingHours = defaultOpeningHours.map(defaultDay => {
        const existingDay = existingHoursByDay[defaultDay.dayOfWeek];
        return existingDay || defaultDay;
      });

      return editingShop;
    }

    // Creating new shop
    const newShopData: CreateCustomShopData = {
      name: `${user?.displayName || "My"} Shop`,
      type: "custom" as const,
      address: "",
      latitude: userLocation?.[0] || 0,
      longitude: userLocation?.[1] || 0,
      tags: [],
      lifetimeHours: 1,
      description: "",
      iconUrl: DEFAULT_CUSTOM_SHOP_ICON,
      backgroundColor: "#ffffff",
      openingHours: defaultOpeningHours,
    };

    return newShopData;
  }, [isEditing, shop, user?.displayName]);

  const [formData, setFormData] = useState<Shop | CreateCustomShopData>(() => getInitialFormData());

  const lifetimeOptions = [
    { hours: 1, label: "1 hour (Free)", price: "Free" },
    { hours: 6, label: "6 hours", price: "10 DKK" },
    { hours: 24, label: "1 day", price: "25 DKK" },
    { hours: 168, label: "1 week", price: "100 DKK" },
  ];

  // Reset form data when modal opens/closes or shop changes
  useEffect(() => {
    if (isOpen) {
      setFormData(getInitialFormData());
      setShowOpeningHours(false);
    }
  }, [isOpen, isEditing, shop]);

  // Initialize address and coordinates for new shops
  useEffect(() => {
    if (isOpen && !isEditing && userLocation) {
      // Set coordinates immediately
      setFormData((prev) => ({
        ...prev,
        latitude: userLocation[0],
        longitude: userLocation[1],
      } as CreateCustomShopData));

      // Get address using OpenStreetMap reverse geocoding
      const getInitialAddress = async () => {
        try {
          const address = await reverseGeocode(userLocation[0], userLocation[1]);
          setInitialAddress(address);
          setFormData((prev) => ({
            ...prev,
            address: address,
          }));
        } catch (error) {
          console.error("Error getting initial address:", error);
          const coordsAddress = `${userLocation[0].toFixed(6)}, ${userLocation[1].toFixed(6)}`;
          setInitialAddress(coordsAddress);
          setFormData((prev) => ({
            ...prev,
            address: coordsAddress,
          }));
        }
      };

      getInitialAddress();
    }
  }, [isOpen, isEditing, userLocation]);

  // Update marker preview only when iconUrl or backgroundColor changes
  useEffect(() => {
    if (markerPreviewRef.current && isOpen && (!isEditing || 'iconUrl' in formData)) {
      // Clear previous content
      markerPreviewRef.current.innerHTML = '';

      // Create marker config to match actual map markers
      const markerConfig: MarkerConfig = {
        size: 120, // Medium size for preview
        icon: "🛍️",
        iconUrl: formData.iconUrl,
        backgroundColor: formData.backgroundColor || "#ffffff",
        showDial: false, // Don't show dial in preview for simplicity
        shadowIntensity: "medium",
        shape: "square" // Custom shops use square markers
      };

      // Create and append marker
      const markerElement = createCustomMarker(markerConfig);
      markerPreviewRef.current.appendChild(markerElement);
    }
  }, [isOpen, isEditing, formData.iconUrl, formData.backgroundColor]);

  const toggleTag = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags?.includes(tag)
        ? prev.tags.filter((t) => t !== tag)
        : [...(prev.tags || []), tag],
    }));
  };

  const updateOpeningHours = (dayIndex: number, field: keyof OpeningHours, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      openingHours: prev.openingHours?.map((day, index) =>
        index === dayIndex ? { ...day, [field]: value } : day
      ),
    }));
  };

  const toggleDayClosed = (dayIndex: number) => {
    setFormData((prev) => ({
      ...prev,
      openingHours: prev.openingHours?.map((day, index) =>
        index === dayIndex
          ? {
            ...day,
            isClosed: !day.isClosed,
            openTime: !day.isClosed ? null : "09:00",
            closeTime: !day.isClosed ? null : "17:00",
          }
          : day
      ),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      alert("You must be logged in to create a custom shop");
      return;
    }

    // Validate form data
    if (!formData.name?.trim()) {
      alert("Please enter a shop name");
      return;
    }

    if (!formData.address?.trim()) {
      alert("Please enter an address");
      return;
    }

    // Validate location based on the data type
    if (!isEditing) {
      // For new shops (CreateCustomShopData), check latitude/longitude
      const newShopData = formData as CreateCustomShopData;
      if (!newShopData.latitude || !newShopData.longitude) {
        alert("Location is required. Please allow location access and try again.");
        return;
      }
    } else {
      // For editing existing shops (Shop), check coordinates array
      const existingShop = formData as Shop;
      if (!existingShop.coordinates || existingShop.coordinates.length !== 2 || !existingShop.coordinates[0] || !existingShop.coordinates[1]) {
        alert("Location is required. Please set a valid location.");
        return;
      }
    }

    onSave(formData);
  };

  const FormContent = () => (

    <div className="flex-1 overflow-y-auto">
      <form onSubmit={handleSubmit} className="space-y-4 p-4 md:p-6 pb-20 md:pb-6">
        {/* Marker Appearance - Only show for create mode or if editing shop has iconUrl support */}
        {(!isEditing || 'iconUrl' in formData) && (
          <div>
            <label className="block text-sm font-medium mb-3">
              <Store className="w-4 h-4 inline mr-1" />
              Marker Appearance
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Marker Preview */}
              <div className="flex flex-col items-center space-y-2">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-300">Preview</div>
                <button
                  type="button"
                  onClick={() => setShowImageBrowser(true)}
                  className="w-32 h-32 flex items-center justify-center rounded-lg transition-all hover:scale-105"
                  title="Click to change icon"
                >
                  <div
                    ref={markerPreviewRef}
                    className="pointer-events-none"
                  />
                </button>
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  Click to change icon
                </p>
              </div>

              {/* Background Color Controls */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    <Palette className="w-4 h-4 inline mr-1" />
                    Background Color
                  </label>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                    {[
                      { name: 'Honey', color: '#D4A574' },
                      { name: 'Vegetable Green', color: '#7CB342' },
                      { name: 'Light Gray', color: '#9E9E9E' },
                      { name: 'Warm Orange', color: '#FF8A65' },
                      { name: 'Sky Blue', color: '#42A5F5' },
                      { name: 'Lavender', color: '#AB47BC' }
                    ].map((colorOption) => (
                      <button
                        key={colorOption.name}
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, backgroundColor: colorOption.color }));
                        }}
                        className={`w-full h-12 rounded-lg border-2 transition-all duration-200 hover:scale-105 ${formData.backgroundColor === colorOption.color
                          ? 'border-blue-500 ring-2 ring-blue-200'
                          : 'border-gray-300 hover:border-gray-400'
                          }`}
                        style={{ backgroundColor: colorOption.color }}
                        title={colorOption.name}
                      >
                        {formData.backgroundColor === colorOption.color && (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* Shop Name */}
        <div>
          <label className="block text-sm font-medium mb-1">Shop Name</label>
          <input
            type="text"
            value={formData.name || ""}
            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
            className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
              }`}
            placeholder="Enter shop name"
            required
          />
        </div>

        {/* Address */}
        <div>
          <label className="block text-sm font-medium mb-1">
            <MapPin className="w-4 h-4 inline mr-1" />
            Address
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={gpsStatus === 'loading'}
              onClick={async () => {
                if (!navigator.geolocation) {
                  alert("Geolocation is not supported by this browser.");
                  return;
                }

                setGpsStatus('loading');

                try {
                  const position = await new Promise<GeolocationPosition>((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject, {
                      enableHighAccuracy: true,
                      timeout: 10000,
                      maximumAge: 60000
                    });
                  });

                  const { latitude, longitude } = position.coords;

                  // Get address using reverse geocoding
                  const address = await reverseGeocode(latitude, longitude);

                  // Update form data with new location and address
                  if (isEditing) {
                    setFormData((prev) => ({
                      ...prev,
                      address,
                      coordinates: [latitude, longitude],
                    } as Shop));
                  } else {
                    setFormData((prev) => ({
                      ...prev,
                      address,
                      latitude,
                      longitude,
                    } as CreateCustomShopData));
                  }

                  // Update initial address for the autocomplete component
                  setInitialAddress(address);

                  setGpsStatus('success');

                  // Reset status after 2 seconds
                  setTimeout(() => setGpsStatus('idle'), 2000);

                } catch (error) {
                  console.error("Error getting location:", error);
                  setGpsStatus('error');
                  alert("Unable to get your location. Please make sure location access is enabled.");

                  // Reset status after 3 seconds
                  setTimeout(() => setGpsStatus('idle'), 3000);
                }
              }}
              className={`flex-shrink-0 px-3 py-2 rounded-lg border transition-all duration-300 ${gpsStatus === 'loading'
                ? 'bg-yellow-100 border-yellow-300 text-yellow-700 animate-pulse'
                : gpsStatus === 'success'
                  ? 'bg-green-100 border-green-300 text-green-700'
                  : gpsStatus === 'error'
                    ? 'bg-red-100 border-red-300 text-red-700'
                    : isDarkMode
                      ? "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${gpsStatus === 'loading' ? 'cursor-not-allowed' : 'cursor-pointer'
                }`}
              title={gpsStatus === 'loading' ? 'Getting your location...' : 'Use my current location'}
            >
              <Navigation className={`w-4 h-4 ${gpsStatus === 'loading' ? 'animate-pulse' : ''
                }`} />
            </button>
            <div className="flex-1">
              <OSMAddressAutocomplete
                initialValue={isEditing ? formData.address || "" : initialAddress}
                onAddressSelect={(address: string, coordinates: [number, number]) => {
                  if (isEditing) {
                    // For editing existing shops, update coordinates array
                    setFormData((prev) => ({
                      ...prev,
                      address,
                      coordinates: [coordinates[0], coordinates[1]],
                    } as Shop));
                  } else {
                    // For creating new shops, update latitude/longitude
                    setFormData((prev) => ({
                      ...prev,
                      address,
                      latitude: coordinates[0],
                      longitude: coordinates[1],
                    } as CreateCustomShopData));
                  }
                }}
                isDarkMode={isDarkMode}
              />
            </div>
          </div>
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium mb-2">
            <Tag className="w-4 h-4 inline mr-1" />
            Tags
          </label>
          <div className="flex flex-wrap gap-2">
            {availableTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => toggleTag(tag)}
                className={`px-3 py-1 rounded-full text-sm border transition-colors ${formData.tags?.includes(tag)
                  ? "bg-blue-600 text-white border-blue-600"
                  : isDarkMode
                    ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                    : "bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200"
                  }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Lifetime Hours - Only for creating new shops */}
        {!isEditing && 'lifetimeHours' in formData && (
          <div>
            <label className="block text-sm font-medium mb-2">
              <Clock className="w-4 h-4 inline mr-1" />
              Shop Lifetime
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {lifetimeOptions.map((option) => (
                <button
                  key={option.hours}
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, lifetimeHours: option.hours }))}
                  className={`p-3 rounded-lg border text-left transition-colors ${formData.lifetimeHours === option.hours
                    ? "border-blue-600 bg-blue-50 dark:bg-blue-900/20"
                    : isDarkMode
                      ? "border-gray-600 hover:bg-gray-700"
                      : "border-gray-300 hover:bg-gray-50"
                    }`}
                >
                  <div className="font-medium">{option.label}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">{option.price}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Description */}
        <div>
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea
            value={formData.description || ""}
            onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
            className={`w-full px-3 py-2 rounded-lg border resize-none ${isDarkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
              }`}
            rows={2}
            placeholder="What are you selling?"
          />
        </div>

        {/* Opening Hours */}
        <div>
          <button
            type="button"
            onClick={() => setShowOpeningHours(!showOpeningHours)}
            className={`w-full flex items-center justify-between p-3 rounded-lg border ${isDarkMode ? "border-gray-600 hover:bg-gray-700" : "border-gray-300 hover:bg-gray-50"
              } transition-colors`}
          >
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span className="text-sm font-medium">Opening Hours {!isEditing && "(optional)"}</span>
            </div>
            <svg
              className={`w-4 h-4 transition-transform ${showOpeningHours ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showOpeningHours && (
            <div className="mt-3 space-y-2">
              {formData.openingHours?.map((day, index) => (
                <div key={day.dayOfWeek} className={`p-3 rounded-lg border ${isDarkMode ? "border-gray-600" : "border-gray-300"
                  }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">{day.dayOfWeek}</span>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={day.isClosed}
                        onChange={() => toggleDayClosed(index)}
                        className="rounded"
                      />
                      <span className="text-sm">Closed</span>
                    </label>
                  </div>

                  {!day.isClosed && (
                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        value={day.openTime || ""}
                        onChange={(e) => updateOpeningHours(index, "openTime", e.target.value)}
                        className={`flex-1 px-2 py-1 text-sm rounded border ${isDarkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                          }`}
                      />
                      <span className="text-sm">to</span>
                      <input
                        type="time"
                        value={day.closeTime || ""}
                        onChange={(e) => updateOpeningHours(index, "closeTime", e.target.value)}
                        className={`flex-1 px-2 py-1 text-sm rounded border ${isDarkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                          }`}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={isSubmitting || !formData.name || !formData.address}
            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            {isEditing ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {isSubmitting ? (isEditing ? "Saving..." : "Creating...") : (
              isEditing ? submitButtonText :
                `${submitButtonText}${!isEditing && 'lifetimeHours' in formData && formData.lifetimeHours && formData.lifetimeHours > 1 ? ` (${lifetimeOptions.find((o) => o.hours === formData.lifetimeHours)?.price})` : ""}`
            )}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <>
      <ResponsiveModal open={isOpen} onOpenChange={onClose}>
        <ResponsiveModalContent className={cn(
          "max-h-[85vh] overflow-hidden flex flex-col",
          isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white"
        )}>
          <ResponsiveModalHeader className={cn(
            "border-b pb-4",
            isDarkMode ? "border-gray-700" : "border-gray-200"
          )}>
            <ResponsiveModalTitle className={isDarkMode ? "text-white" : ""}>
              {modalTitle}
            </ResponsiveModalTitle>
          </ResponsiveModalHeader>

          <div className="flex-1 overflow-y-auto">
            <FormContent />
          </div>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Custom Shop Image Browser Modal */}
      <CustomShopImageBrowserModal
        isOpen={showImageBrowser}
        onClose={() => setShowImageBrowser(false)}
        onSelectImage={(imageUrl) => {
          setFormData(prev => ({ ...prev, iconUrl: imageUrl }));
          setShowImageBrowser(false);
        }}
        isDarkMode={isDarkMode}
      />
    </>
  );
}